import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Minus, Loader, Trophy, Sparkles } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDynamicPricing, whenReady, planIdForCheckout } from "@/lib/subscriptionPricing";
import { checkoutErrorMessage } from "@/components/StripeCheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPARISON_FEATURES = [
  { text: "Sign in to YSVCHI", level: 0 },
  { text: "Join your team or organization", level: 0 },
  { text: "Access all live games on YSVLive", level: 1 },
  { text: "Watch public games and live events", level: 1 },
  { text: "3–5 live events per week", level: 1 },
  { text: "Team library and private games", level: 2 },
  { text: "Create and share clips", level: 2 },
  { text: "Build highlight tapes", level: 2 },
  { text: "Team messaging and schedules", level: 2 },
  { text: "Revenue share and fundraising", level: 3 },
  { text: "Age verification and referee support", level: 3 },
  { text: "Dedicated account manager", level: 3 },
];

const featuresForLevel = level => COMPARISON_FEATURES.map(feature => ({
  text: feature.text,
  included: feature.level <= level,
}));

const buildPlans = (p) => [
  {
    name: "Free Account",
    price: "$0",
    period: "/month",
    desc: "Create an account now and subscribe whenever you're ready.",
    cta: "Create Account",
    ctaStyle: "btn-secondary",
    ctaLink: "/get-started",
    highlight: false,
    features: featuresForLevel(0),
  },
  {
    name: "Fan Account",
    price: p.fan_display,
    period: "/month",
    desc: "For fans who want to stay connected.",
    cta: "Subscribe Now",
    ctaStyle: "btn-primary",
    ctaLink: "/get-started",
    subscriptionTier: "fan",
    features: featuresForLevel(1),
  },
  {
    name: "YSV Member",
    price: p.member_display,
    period: "/month",
    desc: "Game access for players, families, and fans.",
    cta: "Subscribe Now",
    ctaStyle: "btn-primary",
    ctaLink: "/get-started",
    highlight: true,
    badge: "Most Popular",
    subscriptionTier: "member",
    features: featuresForLevel(2),
  },
  {
    name: "Custom",
    price: "Custom",
    period: "For Leagues",
    desc: "Partnership for leagues & organizations",
    cta: "Contact Us",
    ctaStyle: "btn-secondary",
    ctaLink: "/support",
    highlight: false,
    features: featuresForLevel(3),
  },
];

const buildFaqs = (p) => [
  { q: "Can I cancel anytime?", a: "Absolutely. You can cancel your subscription anytime from your YSV profile — your access stays active until the end of your current billing period." },
  { q: "What happens if my payment fails?", a: "Stripe will automatically retry your card up to 4 times over 3 weeks. If all retries fail, your access pauses until you update your payment method from your profile." },
  { q: "What payment methods do you accept?", a: "All major credit cards (Visa, MasterCard, American Express, Discover), Apple Pay, and Google Pay through our secure Stripe checkout." },
  { q: "Do you offer annual billing?", a: "New subscriptions are monthly only for now. Existing annual subscriptions continue on their current terms." },
  { q: "Do you have promo codes?", a: "Yes! Use code SpringBeta26 at checkout to get your first 2 months free. Other promo codes may apply during launches and special events." },
  { q: `What does my ${whenReady(p, p.product_name, "YSV")} subscription unlock?`, a: "Full access to all your team's games, unlimited clips and highlight tapes, downloads, sharing, your player profile page, and team messaging." },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="faq-item" style={{ marginBottom: 8 }}>
      <button className="faq-trigger" data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`} onClick={() => setOpen(!open)}>
        {q}
        <span style={{
          color: 'var(--text-secondary)',
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          fontSize: 18,
        }}>&darr;</span>
      </button>
      {open && <div className="faq-content animate-fade-in">{a}</div>}
    </div>
  );
};

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  // Fan and Member amounts are driven by GET /subscription/plans and Stripe.
  const pricing = useDynamicPricing();
  const { api } = useAuth() || {};
  const plans = buildPlans(pricing);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    // 30 attempts × 2s = 60s of patience. Stripe webhooks usually settle in <5s,
    // but live mode + first-deploy can occasionally lag. Worst case we hit the
    // self-serve /payments/sync-mine endpoint as a last-resort recovery.
    if (attempts >= 30) {
      // Last-resort: force-sync the user's most recent transactions against Stripe.
      // This recovers users when the webhook AND status polling both fail to flip
      // them — e.g. a misconfigured STRIPE_WEBHOOK_SECRET or a Mongo race condition.
      try {
        const token = localStorage.getItem("ysv_token");
        const sync = await axios.post(`${API}/payments/sync-mine`, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (sync.data?.synced_to_active) {
          setPaymentStatus("paid");
          try {
            const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
            if (me.data?.user) localStorage.setItem("ysv_user", JSON.stringify(me.data.user));
          } catch {}
          setTimeout(() => { window.location.href = "/games"; }, 3500);
          return;
        }
      } catch (e) { /* fall through to timeout banner */ }
      setPaymentStatus("timeout");
      return;
    }
    try {
      const token = localStorage.getItem("ysv_token");
      const r = await axios.get(`${API}/payments/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.payment_status === "paid") {
        setPaymentStatus("paid");
        // Refresh local user cache so subscription_status flips immediately
        try {
          const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (me.data?.user) localStorage.setItem("ysv_user", JSON.stringify(me.data.user));
        } catch {}
        // Hold the celebration for ~3.5s, then route to the unlocked games page.
        if (token) setTimeout(() => { window.location.href = "/games"; }, 3500);
        return;
      }
      if (r.data.status === "expired") { setPaymentStatus("expired"); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setPaymentStatus("error"); }
  }, []);

  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) { setPaymentStatus("checking"); pollStatus(sid); }
  }, [searchParams, pollStatus]);

  const handleSubscribe = async (tier) => {
    const token = localStorage.getItem("ysv_token");
    if (!token) { window.location.href = "/login"; return; }
    setSubscribing(true);
    try {
      // Resolve the plan id at click time when our cached pricing didn't come
      // from the backend, so a failed plans fetch can't send an amateur to
      // checkout with the youth plan id (a guaranteed tier-gate 400).
      const client = api();
      const plan_id = await planIdForCheckout(client, pricing, tier);
      const r = await client.post("/payments/checkout", { plan_id, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      alert(checkoutErrorMessage(err));
    }
    setSubscribing(false);
  };

  return (
  <div data-testid="pricing-page" className="page-gradient" style={{ paddingTop: 120 }}>
    {/* Fullscreen celebration overlay on success */}
    {paymentStatus === "paid" && <PaymentSuccessOverlay />}

    {/* Inline status banner for non-success states */}
    {paymentStatus === "checking" && (
      <div style={{ maxWidth: 600, margin: '0 auto 24px', padding: '16px 24px', background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 12, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <Loader size={18} style={{ color: 'var(--cyan)', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: 'var(--cyan)', fontSize: 14 }}>Verifying payment...</p>
      </div>
    )}
    {paymentStatus === "timeout" && (
      <div data-testid="payment-timeout-banner" style={{ maxWidth: 600, margin: '0 auto 24px', padding: '16px 24px', background: 'rgba(255,152,0,0.06)', border: '1px solid rgba(255,152,0,0.3)', borderRadius: 12, textAlign: 'center' }}>
        <p style={{ color: '#ff9800', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Payment is still processing. If your card was charged, click below to sync — otherwise contact support@ysvchi.com.</p>
        <button
          data-testid="payment-manual-sync-btn"
          onClick={async () => {
            try {
              const token = localStorage.getItem("ysv_token");
              const r = await axios.post(`${API}/payments/sync-mine`, {}, { headers: { Authorization: `Bearer ${token}` } });
              if (r.data?.synced_to_active) {
                setPaymentStatus("paid");
                try {
                  const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
                  if (me.data?.user) localStorage.setItem("ysv_user", JSON.stringify(me.data.user));
                } catch {}
                setTimeout(() => { window.location.href = "/games"; }, 3500);
              } else {
                alert(`We couldn't find a paid payment for your account. Reason: ${r.data?.reason || "unknown"}. If you were charged, please contact support@ysvchi.com.`);
              }
            } catch (e) {
              alert("Sync failed: " + (e.response?.data?.detail || e.message));
            }
          }}
          style={{
            padding: '10px 24px', background: 'var(--cyan)', color: '#000',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>
          Sync My Payment
        </button>
      </div>
    )}
    {paymentStatus === "expired" && (
      <div style={{ maxWidth: 600, margin: '0 auto 24px', padding: '16px 24px', background: 'rgba(244,67,54,0.06)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 12, textAlign: 'center' }}>
        <p style={{ color: '#f44336', fontSize: 14, fontWeight: 600 }}>Your checkout session expired before payment completed.</p>
      </div>
    )}

    {/* Hero */}
    <section style={{ textAlign: 'center', padding: '40px 24px 64px', position: 'relative', zIndex: 1 }}>
      <p className="animate-fade-in-up" style={{
        color: 'var(--cyan)', fontSize: 13, fontWeight: 600,
        letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 20,
      }}>PRICING</p>
      <h1 className="animate-fade-in-up stagger-1" style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 'clamp(32px, 5vw, 54px)',
        fontWeight: 800, lineHeight: 1.1, marginBottom: 16,
      }}>Simple, Transparent Pricing</h1>
      <p className="animate-fade-in-up stagger-2" style={{
        color: 'var(--text-secondary)', fontSize: 16,
      }}>We film all games, upload for you to Relive the Game.</p>
    </section>

    {/* Plans */}
    <section style={{
      width: '100%',
      overflowX: 'auto',
      padding: '16px 24px 80px',
      WebkitOverflowScrolling: 'touch',
      scrollSnapType: 'x proximity',
      scrollbarColor: 'var(--cyan) #111114',
      boxSizing: 'border-box',
    }}>
      <div style={{
        width: '100%',
        minWidth: 1454,
        maxWidth: 1480,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(350px, 1fr))',
        gap: 18,
        alignItems: 'stretch',
      }}>
      {plans.map((plan, i) => (
        <div
          key={plan.name}
          data-testid={`pricing-card-${i}`}
          className={`glass-card animate-fade-in-up ${plan.highlight ? 'pricing-highlight' : ''}`}
          style={{
            padding: 28,
            opacity: 0,
            animationDelay: `${i * 0.15}s`,
            animationFillMode: 'forwards',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 690,
            boxSizing: 'border-box',
            scrollSnapAlign: 'start',
          }}
        >
          {plan.badge && (
            <span style={{
              position: 'absolute',
              top: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--cyan)',
              color: '#000',
              fontSize: 11,
              fontWeight: 700,
              padding: '4px 14px',
              borderRadius: 20,
              letterSpacing: '0.03em',
            }}>{plan.badge}</span>
          )}

          <div style={{ height: 190, flexShrink: 0 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{plan.name}</h3>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, height: 58, whiteSpace: 'nowrap' }}>
              <span style={{
                fontSize: plan.price === "Custom" ? 36 : 48,
                fontWeight: 800,
                fontFamily: "'Outfit', sans-serif",
                color: 'var(--cyan)',
              }} data-testid={plan.subscriptionTier ? `pricing-${plan.subscriptionTier}-price` : undefined}>
                {plan.subscriptionTier ? whenReady(pricing, plan.price) : plan.price}
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{plan.period}</span>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: '20px', minHeight: 20, marginBottom: 6 }}>{plan.desc}</p>
            <p style={{
              color: 'var(--text-secondary)', fontSize: 12, marginBottom: 24,
              visibility: plan.subscriptionTier ? 'visible' : 'hidden',
            }}>
              Cancel anytime. Auto-renews monthly.
            </p>
          </div>

          {plan.subscriptionTier ? (
            <button data-testid={`pricing-cta-${i}`} onClick={() => handleSubscribe(plan.subscriptionTier)} disabled={subscribing || !pricing.ready} className="btn-primary" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              height: 52, padding: 0, boxSizing: 'border-box', marginBottom: 28, width: '100%',
              border: 'none', cursor: 'pointer', opacity: (subscribing || !pricing.ready) ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}>{subscribing ? 'Redirecting to checkout...'
                : !pricing.ready ? 'Loading plans…'
                : `Subscribe ${plan.subscriptionTier === "fan" ? pricing.fan_label : pricing.member_label}`}</button>
          ) : (
            <Link to={plan.ctaLink} data-testid={`pricing-cta-${i}`} className={plan.ctaStyle} style={{
              textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 52, padding: 0, boxSizing: 'border-box', textAlign: 'center', marginBottom: 28, width: '100%',
              whiteSpace: 'nowrap',
            }}>{plan.cta}</Link>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {plan.features.map(f => (
              <div
                key={f.text}
                aria-label={`${f.text}: ${f.included ? "included" : "not included"}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  opacity: f.included ? 1 : 0.32,
                }}
              >
                {f.included === true ? (
                  <Check size={16} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                ) : (
                  <Minus size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                )}
                <span style={{
                  color: f.included ? 'var(--text-secondary)' : '#70707a',
                  fontSize: 13,
                }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      </div>
    </section>

    {/* FAQ */}
    <section style={{
      maxWidth: 700,
      margin: '0 auto',
      padding: '0 24px 80px',
    }}>
      <div className="glow-line" style={{ marginBottom: 48 }} />
      <h2 data-testid="faq-heading" style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 28,
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: 32,
      }}>Frequently Asked Questions</h2>
      {buildFaqs(pricing).map(f => <FAQItem key={f.q} q={f.q} a={f.a} />)}
    </section>

    {/* Bottom CTA */}
    <section style={{
      textAlign: 'center',
      padding: '0 24px 80px',
    }}>
      <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 20 }}>
        Contact us and we'll help you choose the right plan.
      </p>
      <Link to="/support" data-testid="pricing-contact-btn" className="btn-secondary" style={{
        textDecoration: 'none', padding: '12px 28px', fontSize: 14,
      }}>Contact Us</Link>
    </section>
  </div>
  );
};

/* ═══ Fullscreen "Welcome to YSV" celebration ═══ */
const PaymentSuccessOverlay = () => {
  // Confetti dot positions/colors generated once
  const dots = React.useMemo(() => {
    const colors = ['#00e5ff', '#ff6b00', '#4caf50', '#ffd54f', '#ff4081'];
    return Array.from({ length: 60 }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 1.6 + Math.random() * 1.4,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 6,
      drift: (Math.random() - 0.5) * 200,
    }));
  }, []);

  return (
    <div data-testid="payment-success" role="dialog" aria-label="Payment successful" style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'radial-gradient(ellipse at center, rgba(0,40,30,0.95) 0%, rgba(0,0,0,0.98) 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      animation: 'ysv-success-fade 0.4s ease-out',
      overflow: 'hidden',
    }}>
      {/* Confetti */}
      {dots.map((d, i) => (
        <span key={i} style={{
          position: 'absolute', top: -20, left: `${d.left}%`,
          width: d.size, height: d.size, borderRadius: i % 3 === 0 ? '50%' : 2,
          background: d.color,
          animation: `ysv-confetti ${d.duration}s ${d.delay}s linear forwards`,
          ['--drift']: `${d.drift}px`,
        }} />
      ))}

      {/* Trophy ring + glow */}
      <div style={{ position: 'relative', marginBottom: 28 }}>
        <div style={{
          position: 'absolute', inset: -16, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(76,175,80,0.45) 0%, transparent 70%)',
          animation: 'ysv-pulse 1.6s ease-in-out infinite',
        }} />
        <div style={{
          position: 'relative', width: 130, height: 130, borderRadius: '50%',
          background: 'linear-gradient(135deg, #4caf50 0%, #00e5ff 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 60px rgba(0,229,255,0.4), 0 0 120px rgba(76,175,80,0.3)',
          animation: 'ysv-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          transform: 'scale(0)',
        }}>
          <Trophy size={64} style={{ color: '#fff', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }} />
        </div>
      </div>

      <p data-testid="payment-success-eyebrow" style={{
        color: '#4caf50', fontSize: 13, fontWeight: 800,
        letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 12,
        animation: 'ysv-rise 0.5s 0.3s ease-out backwards',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Sparkles size={14} /> Payment Successful <Sparkles size={14} />
      </p>

      <h1 style={{
        fontFamily: "'Outfit', sans-serif", fontWeight: 900,
        fontSize: 'clamp(34px, 6vw, 64px)', lineHeight: 1.05,
        textAlign: 'center', marginBottom: 12, padding: '0 24px',
        background: 'linear-gradient(135deg, #ffffff 20%, #00e5ff 60%, #4caf50 100%)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        backgroundClip: 'text', color: '#fff',
        animation: 'ysv-rise 0.6s 0.45s ease-out backwards',
      }}>
        Welcome, YSV Member!
      </h1>

      <p style={{
        color: 'rgba(255,255,255,0.75)', fontSize: 16, textAlign: 'center',
        maxWidth: 460, padding: '0 24px', marginBottom: 28,
        animation: 'ysv-rise 0.6s 0.6s ease-out backwards',
      }}>
        Your subscription is active. Time to <strong style={{ color: '#fff' }}>Relive the Game</strong>.
      </p>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        animation: 'ysv-rise 0.6s 0.75s ease-out backwards',
      }}>
        <Loader size={14} style={{ color: 'var(--cyan)', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em' }}>
          Taking you to your games…
        </p>
      </div>

      <style>{`
        @keyframes ysv-success-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ysv-pop {
          0% { transform: scale(0) rotate(-180deg); }
          70% { transform: scale(1.15) rotate(10deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes ysv-pulse {
          0%, 100% { transform: scale(0.95); opacity: 0.7; }
          50% { transform: scale(1.15); opacity: 1; }
        }
        @keyframes ysv-rise {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ysv-confetti {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--drift, 0), 110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
